import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore.js';
import { SEO } from '../components/seo/index.js';
import { NavBar } from '../components/shared/NavBar.js';
import { VideoPreloader } from '../components/shared/VideoPreloader.js';
import { AppDeletionBanner } from '../components/settings/privacy/AppDeletionBanner.js';
import { MaintenanceNoticeHost } from '../components/shared/MaintenanceNoticeHost.js';
import { Footer } from '../components/landing/Footer.js';
import { isReportPrintMode } from '../lib/printMode.js';

function shouldHideAppFooter(pathname: string): boolean {
  if (pathname === '/interview/setup') return true;
  if (/^\/interview\/[^/]+\/stage\/\d+\/feedback$/.test(pathname)) return true;
  if (/^\/interview\/[^/]+\/report$/.test(pathname)) return true;
  return false;
}

export function AppLayout() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const user = useAuthStore((s) => s.user);
  const hideFooter = shouldHideAppFooter(pathname);

  // Print mode (Gotenberg PDF render): strip all app chrome so the PDF contains
  // only the report content. The page handles its own data fetching + theming.
  if (isReportPrintMode(pathname, search)) {
    return <Outlet />;
  }

  // New accounts (any provider — email or Google) must go through the welcome
  // wizard first: while welcomeCompletedAt is NULL in the DB, every app page
  // routes to /welcome. Strictly `=== null` — undefined means a stale cached
  // user object from before the field existed, which must not cause a bounce.
  if (user && user.welcomeCompletedAt === null) {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-ig-bg-primary text-ig-text-primary">
      <SEO noindex title={t('dashboard.pageTitle', { defaultValue: 'Dashboard' })} description="Private InterviewGator dashboard." />
      <VideoPreloader />
      <NavBar variant="app" />
      <AppDeletionBanner />
      <MaintenanceNoticeHost />
      <main className="flex-1 px-0 md:px-8">
        <Outlet />
      </main>
      {!hideFooter ? <Footer /> : null}
    </div>
  );
}
